"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { FormEvent, useEffect, useState } from "react";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { nameFromEmail, refreshAuthSession, writeAuthSession } from "@/lib/auth-session";
import { apiErrorCode, apiErrorMessage } from "@/lib/api-error";
import styles from "./auth-page.module.css";

type AuthMode = "login" | "register";
type LoginMethod = "password" | "code";

const DEMO_CODE = "246810";
const allowPrototypeEmailCode = process.env.NODE_ENV !== "production";

function safeInternalNext(value: string | null) {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.includes("\\")) {
    return "/user-center";
  }
  return value;
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.brandIcon}>
      <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.41Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.62-2.36l-3.24-2.54c-.9.6-2.05.96-3.38.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.62A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.39 13.93A6.02 6.02 0 0 1 6.08 12c0-.67.11-1.32.31-1.93V7.45H3.04A10 10 0 0 0 2 12c0 1.62.39 3.15 1.04 4.55l3.35-2.62Z" />
      <path fill="#EA4335" d="M12 5.94c1.47 0 2.79.5 3.82 1.5l2.87-2.87A9.64 9.64 0 0 0 12 2a10 10 0 0 0-8.96 5.45l3.35 2.62C7.18 7.7 9.39 5.94 12 5.94Z" />
    </svg>
  );
}

function DiscordIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.brandIcon}>
      <path
        fill="#5865F2"
        d="M19.27 4.74A16.4 16.4 0 0 0 15.16 3.5l-.5 1.03a15.2 15.2 0 0 0-5.31 0L8.83 3.5c-1.44.25-2.82.67-4.1 1.24C2.14 8.58 1.44 12.32 1.8 16a16.6 16.6 0 0 0 5.04 2.54l1.21-1.65a10.2 10.2 0 0 1-1.9-.91l.47-.36c3.67 1.7 7.65 1.7 11.28 0l.48.36c-.61.36-1.25.67-1.91.91l1.21 1.65A16.5 16.5 0 0 0 22.2 16c.43-4.27-.73-7.98-2.93-11.26ZM8.67 13.74c-1.1 0-2-1.02-2-2.27 0-1.26.88-2.28 2-2.28 1.13 0 2.03 1.03 2 2.28 0 1.25-.89 2.27-2 2.27Zm6.66 0c-1.1 0-2-1.02-2-2.27 0-1.26.88-2.28 2-2.28 1.13 0 2.03 1.03 2 2.28 0 1.25-.87 2.27-2 2.27Z"
      />
    </svg>
  );
}

function SocialButtons({ action }: { action: "Log in" | "Sign up" }) {
  const [message, setMessage] = useState("");

  return (
    <>
      <div className={styles.socials}>
        <button type="button" className={styles.social} onClick={() => setMessage("Google sign-in will be available after OAuth is connected.")}>
          <GoogleIcon />
          <span>Google</span>
        </button>
        <button type="button" className={styles.social} onClick={() => setMessage("Discord sign-in will be available after OAuth is connected.")}>
          <DiscordIcon />
          <span>Discord</span>
        </button>
      </div>
      <p className={styles.message} aria-live="polite">{message ? `${action}: ${message}` : ""}</p>
    </>
  );
}

export function AuthPage({ mode }: { mode: AuthMode }) {
  const isRegister = mode === "register";
  const router = useRouter();
  const searchParams = useSearchParams();
  const [loginMethod] = useState<LoginMethod>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    let active = true;
    refreshAuthSession()
      .then((session) => {
        if (active && session) router.replace("/user-center");
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [router]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setInterval(() => setCountdown((value) => value - 1), 1000);
    return () => window.clearInterval(timer);
  }, [countdown]);

  const report = (nextMessage: string, error = false) => {
    setMessage(nextMessage);
    setIsError(error);
  };

  const sendCode = () => {
    if (!allowPrototypeEmailCode) {
      report("Email code sign-in is not available.", true);
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      report("Enter a valid email address before requesting a code.", true);
      return;
    }
    setCountdown(60);
    report(`Prototype verification code: ${DEMO_CODE}`);
  };

  const finish = (nextEmail = email, nextName = nameFromEmail(email)) => {
    writeAuthSession({ email: nextEmail, name: nextName });
    const next = searchParams.get("next");
    router.push(safeInternalNext(next));
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      report("Enter a valid email address.", true);
      return;
    }

    if (isRegister) {
      if (password.length < 8) {
        report("Password must be at least 8 characters.", true);
        return;
      }
      if (password !== confirmPassword) {
        report("The two passwords do not match.", true);
        return;
      }
      if (allowPrototypeEmailCode && code !== DEMO_CODE) {
        report(`Enter the prototype verification code ${DEMO_CODE}.`, true);
        return;
      }
      if (!acceptedTerms) {
        report("You must agree to the Terms of Service and Privacy Policy.", true);
        return;
      }
    } else if (loginMethod === "password" && password.length < 8) {
      report("Enter a password with at least 8 characters.", true);
      return;
    } else if (loginMethod === "code" && (!allowPrototypeEmailCode || code !== DEMO_CODE)) {
      report(`Enter the prototype verification code ${DEMO_CODE}.`, true);
      return;
    }

    report("");
    if (allowPrototypeEmailCode && !isRegister && loginMethod === "code") {
      finish();
      return;
    }

    try {
      const response = await fetch(isRegister ? "/api/auth/register" : "/api/auth/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, displayName: nameFromEmail(email) }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.ok) {
        const code = apiErrorCode(payload, "ACCOUNT_REQUEST_FAILED");
        const fallbackMessage =
          code === "CUSTOMER_EMAIL_EXISTS" || code === "CUSTOMER_REGISTER_UNAVAILABLE"
            ? "This email already has a PRVBUY account. Please log in instead."
            : code === "CUSTOMER_PASSWORD_INVALID" || code === "CUSTOMER_CREDENTIALS_INVALID"
              ? "Email or password is incorrect."
              : code === "CUSTOMER_NOT_FOUND"
                ? "No PRVBUY account exists for this email yet."
                : apiErrorMessage(payload, response.status, "The account request could not be completed.");
        report(fallbackMessage, true);
        return;
      }

      finish(payload.user?.email ?? email, payload.user?.displayName ?? nameFromEmail(email));
    } catch {
      report("Account service is not reachable. Please try again.", true);
    }
  };

  return (
    <main className={styles.page}>
      <div className={styles.shell}>
        <aside className={styles.brand}>
          <Image
            src="/images/hero/prvbuy-cross-border-studio-hero-logo-clean-4k-v4.png"
            alt=""
            fill
            priority
            sizes="100vw"
            className={styles.brandImage}
          />
          <div className={styles.brandShade} />
          <div className={styles.brandContent}>
            <Link href="/" className={styles.logo}>
              <span className={styles.logoMark}>
                <Image src="/images/prvbuy-logo-p-flat.png" alt="" width={42} height={42} />
              </span>
              <span>PRVBUY</span>
            </Link>
            <div className={styles.brandCopy}>
              <h2>China shopping,<br />handled.</h2>
              <p>We buy, inspect and combine your orders before they ship worldwide.</p>
              <div className={styles.process} aria-label="PRVBUY service process">
                {["Buy", "Check", "Pack", "Ship"].map((step) => <span key={step}>{step}</span>)}
              </div>
            </div>
          </div>
        </aside>

        <section className={`${styles.formSide} ${isRegister ? styles.compact : ""}`}>
          <div className={styles.formWrap}>
            <div className={styles.authBackNav}>
              <Link href="/" className={styles.back}>
                <ArrowLeft size={16} /> Back to home
              </Link>
            </div>

            <header className={styles.heading}>
              <span className={styles.eyebrow}>{isRegister ? "Join PRVBUY" : "Welcome back"}</span>
              <h1>{isRegister ? "Create your account" : "Continue your journey"}</h1>
              <p>
                {isRegister ? (
                  <>Already have an account? <Link href="/login">Log in</Link></>
                ) : (
                  <>New to PRVBUY? <Link href="/register">Create an account</Link></>
                )}
              </p>
            </header>

            <form className={styles.form} onSubmit={submit} noValidate>
              <div className={styles.field}>
                <label htmlFor={`${mode}-email`}>Email address</label>
                <input id={`${mode}-email`} className={styles.input} type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Enter your email address" />
              </div>

              {isRegister || loginMethod === "password" ? (
                <div className={isRegister ? styles.registerPair : undefined}>
                  <div className={styles.field}>
                    <label htmlFor={`${mode}-password`}>Password</label>
                    <input id={`${mode}-password`} className={styles.input} type={showPassword ? "text" : "password"} autoComplete={isRegister ? "new-password" : "current-password"} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="At least 8 characters" />
                    <button type="button" className={styles.iconAction} onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {isRegister ? (
                    <div className={styles.field}>
                      <label htmlFor="register-confirm-password">Confirm password</label>
                      <input id="register-confirm-password" className={styles.input} type={showConfirmPassword ? "text" : "password"} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Repeat password" />
                      <button type="button" className={styles.iconAction} onClick={() => setShowConfirmPassword((value) => !value)} aria-label={showConfirmPassword ? "Hide confirmed password" : "Show confirmed password"}>
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {allowPrototypeEmailCode && (loginMethod === "code" || isRegister) ? (
                <div className={isRegister ? styles.registerPair : undefined}>
                  {isRegister ? (
                    <div className={styles.field}>
                      <label htmlFor="register-invitation">Invitation code <span>(optional)</span></label>
                      <input id="register-invitation" className={styles.input} type="text" autoComplete="off" placeholder="Enter code" />
                    </div>
                  ) : null}
                  <div className={styles.field}>
                    <label htmlFor={`${mode}-verification`}>Verification code</label>
                    <input id={`${mode}-verification`} className={`${styles.input} ${styles.inputWithAction}`} inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value)} placeholder="Enter code" />
                    <button type="button" className={styles.fieldAction} onClick={sendCode} disabled={countdown > 0}>
                      {countdown > 0 ? `${countdown}s` : "Send code"}
                    </button>
                  </div>
                </div>
              ) : null}

              {!isRegister && loginMethod === "password" ? (
                <div className={styles.formMeta}>
                  <button type="button" onClick={() => report("Password reset will be available after email delivery is connected.")}>Forgot password?</button>
                </div>
              ) : null}

              {isRegister ? (
                <div className={styles.agreement}>
                  <input id="register-agreement" type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} />
                  <p>
                    <label htmlFor="register-agreement">I have read and agree to the </label>
                    <Link href="/terms" className={styles.inlineLink}>Terms of Service</Link> and{" "}
                    <Link href="/privacy" className={styles.inlineLink}>Privacy Policy</Link>.
                  </p>
                </div>
              ) : null}

              <p className={`${styles.message} ${isError ? styles.error : ""}`} aria-live="polite">{message}</p>
              <button type="submit" className={styles.primary}>
                <span>{isRegister ? "Create account" : "Log in"}</span>
              </button>
              <div className={styles.divider}>or continue with</div>
              <SocialButtons action={isRegister ? "Sign up" : "Log in"} />
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}
